import { getMe } from '@/lib/api';
import type { MeResponse } from '@/lib/types';
import { type ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';

interface AuthState {
  loading: boolean;
  me: MeResponse | null;
  error: string | null;
}

const AuthCtx = createContext<{
  state: AuthState;
  refresh: () => Promise<void>;
} | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ loading: true, me: null, error: null });

  const refresh = useCallback(async () => {
    try {
      const me = await getMe();
      setState({ loading: false, me, error: null });
    } catch (err: unknown) {
      const apiErr = err as { status?: number; message?: string };
      if (apiErr.status === 401) {
        setState({ loading: false, me: null, error: null });
      } else {
        setState({ loading: false, me: null, error: apiErr.message ?? 'unknown' });
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return <AuthCtx.Provider value={{ state, refresh }}>{children}</AuthCtx.Provider>;
}

export function useAuthContext() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuthContext must be used inside AuthProvider');
  return ctx;
}
